/**
 * Mic pre-warm — request the user's microphone permission *before* the user
 * actually taps to record. Once permission is granted, the stream is closed
 * immediately; the *permission* persists for the rest of the session, so the
 * next `getUserMedia()` call resolves instantly with no permission prompt.
 *
 * Browsers don't ship a public API for "is mic permission already granted",
 * so we use the Permissions API where available and otherwise just call
 * `getUserMedia` and immediately stop the resulting tracks.
 */

const PRE_WARM_KEY = "flowtasks_mic_prewarmed_at";
const PRE_WARM_TTL_MS = 1000 * 60 * 60 * 12; // 12h — keeps the warm state fresh

export function isPreWarmed() {
  try {
    const at = parseInt(localStorage.getItem(PRE_WARM_KEY) || "0", 10);
    if (!at) return false;
    return Date.now() - at < PRE_WARM_TTL_MS;
  } catch {
    return false;
  }
}

export async function preWarmMic() {
  if (!navigator.mediaDevices?.getUserMedia) return false;
  // Honour an existing granted permission via Permissions API (Chromium)
  try {
    if (navigator.permissions?.query) {
      const res = await navigator.permissions.query({ name: "microphone" });
      if (res.state === "granted") {
        localStorage.setItem(PRE_WARM_KEY, String(Date.now()));
        return true;
      }
      if (res.state === "denied") return false;
    }
  } catch {
    // ignore — fall through to getUserMedia path
  }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    // Immediately stop — we just wanted the permission.
    stream.getTracks().forEach((t) => t.stop());
    localStorage.setItem(PRE_WARM_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}
